import {
  ACTION_TYPE_TITLES,
  ActionTypeTitlesType,
  ApprovalChainTemplateType,
  ApprovalsOrganizationBusinessOption,
  ApprovalsOrganizationUnitActiveType,
  ApprovalTableRowModelType,
} from '@approvals/graphql';

function getAvailableActionTypes(): ActionTypeTitlesType[] {
  return Object.keys(ACTION_TYPE_TITLES) as ActionTypeTitlesType[];
}

export function getActionTypeTitle(key: ActionTypeTitlesType) {
  return ACTION_TYPE_TITLES[key];
}

function isOddNumber(n: number) {
  return !!(n % 2);
}

function getIsPreApproved(
  unit: ApprovalsOrganizationUnitActiveType,
  actionType: ActionTypeTitlesType
) {
  return (
    !!unit.preapprovalActions?.length &&
    unit.preapprovalActions.includes(actionType)
  );
}

export function filteredApprovalChainTemplatesByActionType(
  approvalChainTemplates: ApprovalChainTemplateType[],
  actionType: ActionTypeTitlesType
) {
  return approvalChainTemplates.filter(
    (chain: { actionType?: string }) => chain.actionType === actionType
  );
}

export function buildUnitModelByType({
  unit,
  unitIndex,
  actionTypeKey,
  organizationBusiness,
}: {
  unit?: ApprovalsOrganizationUnitActiveType;
  unitIndex?: number;
  actionTypeKey?: ActionTypeTitlesType;
  organizationBusiness?: ApprovalsOrganizationBusinessOption;
}): ApprovalTableRowModelType | null {
  if (!unit || !actionTypeKey) {
    return null;
  }

  return {
    key: `${actionTypeKey}__${unit.id}`,
    preApprovedActions: unit.preapprovalActions || [],
    isPreApproved: getIsPreApproved(unit, actionTypeKey),
    unitId: unit.id,
    unitName: unit.name,
    costCenter: unit.costCenter || '',
    approvalChainTemplates: filteredApprovalChainTemplatesByActionType(
      unit.approvalChainTemplates,
      actionTypeKey
    ),
    actionTypeKey,
    organizationBusinessId: organizationBusiness?.id || '',
    organizationBusinessName: organizationBusiness?.name || '',
    odd: isOddNumber(unitIndex || 0),
  };
}

export function getTransformedDataSource(
  organizationUnits: ApprovalsOrganizationUnitActiveType[],
  organizationBusiness?: ApprovalsOrganizationBusinessOption
) {
  return [...organizationUnits]
    .sort(getSortCallbackBy('name'))
    .reduce((acc, unit, unitIndex) => {
      const actionTypes = getAvailableActionTypes();

      actionTypes.forEach((actionTypeKey) => {
        const row = buildUnitModelByType({
          unit,
          unitIndex,
          actionTypeKey,
          organizationBusiness,
        });

        if (row) acc.push(row);
      });
      return acc;
    }, [] as ApprovalTableRowModelType[]);
}

export function getSortCallbackBy<T>(
  param: keyof T,
  orientation?: 'descend' | 'ascend'
) {
  const isDescend = orientation === 'descend';

  return (a: T, b: T) => {
    if (a[param] > b[param]) {
      return isDescend ? -1 : 1;
    }

    if (a[param] < b[param]) {
      return isDescend ? 1 : -1;
    }

    return 0;
  };
}

export function getApprovalChainTitle(letter: string) {
  return `Approval Chain ${letter}${letter === 'A' ? ' (Default)' : ''}`;
}

export function getChainingColumnsQuantity(
  organizationUnits: ApprovalsOrganizationUnitActiveType[] = []
) {
  const approvalChainTemplatesSizes = organizationUnits.reduce((acc, unit) => {
    const actionTypes = getAvailableActionTypes();

    actionTypes.forEach((actionType) => {
      const approvalChainTemplateslengthByType =
        unit.approvalChainTemplates.filter(
          (item: { actionType?: string }) => item.actionType === actionType
        ).length;

      acc.push(approvalChainTemplateslengthByType as never);
    });
    return acc;
  }, []);

  return (
    (approvalChainTemplatesSizes &&
      approvalChainTemplatesSizes.length > 0 &&
      Math.max(...approvalChainTemplatesSizes)) ||
    1
  );
}

export function getLetterSignByNumber(i: number): string {
  const LETTERS_QUANTITY = 26;

  if (i < LETTERS_QUANTITY) {
    return String.fromCharCode('A'.charCodeAt(0) + i);
  }

  const mod = i % LETTERS_QUANTITY;
  const dividedIn = Math.floor(i / LETTERS_QUANTITY);

  return `${getLetterSignByNumber(dividedIn - 1)}${getLetterSignByNumber(mod)}`;
}

export function getRandomKey() {
  return `${Math.floor(new Date().getTime() * Math.random())}`;
}

export function getNumberPositionSufix(n: number): string {
  const sufix = { 1: 'st', 2: 'nd', 3: 'rd' }[n] || 'th';
  return `${n}${sufix}`;
}
